import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { Domain } from '@/types/navigation';

interface DomainRouteGuardProps {
  allowedDomain: Domain | Domain[];
  children: React.ReactNode;
}

/**
 * Domain Route Guard Component
 *
 * Enforces domain-based access control for routes.
 * If user's domain doesn't match the allowed domain(s), redirects to 404.
 *
 * This is a critical security component that prevents cross-domain access.
 *
 * @param allowedDomain - The domain or array of domains allowed to access this route
 * @param children - The child components to render if domain matches
 */
export function DomainRouteGuard({ allowedDomain, children }: DomainRouteGuardProps) {
  const { userDomain, isLoading } = useAuth();

  // Wait for auth to fully resolve before checking domain — prevents false 404s on page refresh
  if (isLoading) {
    return null;
  }

  const allowed = Array.isArray(allowedDomain) ? allowedDomain : [allowedDomain];
  if (!allowed.includes(userDomain)) {
    // Return 404 - do NOT redirect to user's dashboard
    // This prevents information leakage about other domains
    return <Navigate to="/404" replace />;
  }

  return <>{children}</>;
}
